/* eslint-disable no-sequences */
import React from 'react';
import PropTypes from 'prop-types';
import NumberFormat from 'react-number-format';
import styled, { css } from 'styled-components';
import { format } from 'date-fns';

import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
} from '@material-ui/core';
import {
  Error as ErrorIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  ReportProblem as ReportProblemIcon,
  CancelPresentation as CancelPresentationIcon,
} from '@material-ui/icons';

import { Colors } from '../../../../../../constants';
import { DueStatus } from '../../../../../../constants/stock/enums';

const SpecificationsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: 32px;
`;

const SpecificationsTableContainer = styled.div`
  border: 1px solid ${Colors.GRAY_LIGHT};
  background-color: ${Colors.WHITE};
  border-radius: 8px;
  overflow: hidden;
`;

const Subtitle = styled.p`
  width: 100%;
  font-size: 20px;
  margin-top: 32px;
  color: ${Colors.SECONDARY_MEDIUM};
  font-weight: 400;
`;

const RowNameText = styled(TableCell)`
  color: ${Colors.SECONDARY_DARK};
  font-weight: bold;
  background-color: ${Colors.WHITE};
  font-family: 'nunitoregular';
`;

const RowBodyText = styled(TableCell)`
  font-family: 'nunitoregular';
`;

const TableContainerStyle = {
  maxHeight: '385px',
  overflowY: 'auto',
  display: 'block',
};

const StatusContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  font-weight: 700;
`;

const StatusIcon = styled.div`
  display: flex;
  align-items: center;

  svg {
    font-size: 22px;
    color: ${({ color }) => color};
  }
`;

const Row = styled(TableRow)`
  ${({ duestatus }) =>
    duestatus === DueStatus.EXPIRED || duestatus === DueStatus.TRUNCATED
      ? css`
          svg {
            color: ${Colors.ERROR_DARK};
          }
          background-color: ${Colors.DANGER_SHADES};
        `
      : duestatus === DueStatus.WARNING
      ? css`
          svg {
            color: ${Colors.WARNING};
          }
          background-color: ${Colors.WARNING_SHADES};
        `
      : duestatus === DueStatus.MISSING_PRODUCT
      ? css`
          svg {
            color: ${Colors.INFO_DARK};
          }
          background-color: ${Colors.INFO_SHADES};
        `
      : ''}
  &:last-child td {
    border-bottom: none;
  }
`;

const renderStatusWithIcon = (dueStatus) => {
  switch (dueStatus) {
    case DueStatus.EXPIRED:
      return (
        <StatusContainer>
          <StatusIcon color={Colors.ERROR_DARK}>
            <ErrorIcon />
          </StatusIcon>
          Vencido
        </StatusContainer>
      );
    case DueStatus.WARNING:
      return (
        <StatusContainer>
          <StatusIcon color={Colors.WARNING}>
            <WarningIcon />
          </StatusIcon>
          Data crítica
        </StatusContainer>
      );
    case DueStatus.VALID:
      return (
        <StatusContainer>
          <StatusIcon color={Colors.SUCCESS_DARK}>
            <CheckCircleIcon />
          </StatusIcon>
          Válido
        </StatusContainer>
      );
    case DueStatus.TRUNCATED:
      return (
        <StatusContainer>
          <StatusIcon color={Colors.ERROR_DARK}>
            <ErrorIcon />
          </StatusIcon>
          Danificado
        </StatusContainer>
      );
    case DueStatus.MISSING_PRODUCT:
      return (
        <StatusContainer>
          <StatusIcon color={Colors.INFO_DARK}>
            <CancelPresentationIcon />
          </StatusIcon>
          Em falta
        </StatusContainer>
      );
    default:
      return '-';
  }
};

const handleEntryDate = (entryDate) => {
  if (!entryDate) return 'Sem data de entrada';
  const date = new Date(entryDate);
  return `${format(date, 'dd/MM/yyyy')} às ${format(date, 'HH:mm')}`;
};

const Batches = ({ ...props }) => {
  const { batchesData } = props;
  return (
    <>
      <SpecificationsWrapper>
        <Subtitle>Lotes armazenados</Subtitle>

        <SpecificationsTableContainer>
          <TableContainer style={TableContainerStyle}>
            <Table stickyHeader aria-label="simple table">
              <TableHead>
                <TableRow>
                  <RowNameText>Lote</RowNameText>
                  <RowNameText>Data de entrada</RowNameText>
                  <RowNameText>Quantidade</RowNameText>
                  <RowNameText>Validade</RowNameText>
                  <RowNameText>Status</RowNameText>
                </TableRow>
              </TableHead>

              <TableBody>
                {batchesData.length > 0 ? (
                  batchesData.map((item) => (
                    <Row key={item.id} duestatus={item.dueStatus}>
                      <RowBodyText>{item.name || '-'}</RowBodyText>
                      <RowBodyText align="left">{handleEntryDate(item.entryDate)}</RowBodyText>
                      <RowBodyText align="left">
                        <NumberFormat
                          value={item.availableQuantity || 0}
                          displayType="text"
                          decimalSeparator=","
                          thousandSeparator="."
                        />
                      </RowBodyText>
                      <RowBodyText align="left">
                        {item.dueDate
                          ? format(new Date(item.dueDate), 'dd/MM/yyyy')
                          : 'Sem validade'}
                      </RowBodyText>

                      <RowBodyText align="left">{renderStatusWithIcon(item.dueStatus)}</RowBodyText>
                    </Row>
                  ))
                ) : (
                  <Row>
                    <TableCell align="left" colSpan={4}>
                      Nenhum lote cadastrado.
                    </TableCell>
                  </Row>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </SpecificationsTableContainer>
      </SpecificationsWrapper>
    </>
  );
};

Batches.propTypes = {
  batchesData: PropTypes.array.isRequired,
};

export default Batches;
